import React, { useCallback, useEffect, useState } from "react";
import {  message } from "antd";
import { MapPin, Building, Home, Edit2, MapPinIcon, Trash2 } from "lucide-react";

import { useNotification } from "../../Toast/Notification";
import { useAuth } from "../../AuthContext/AuthContext";
import SecondaryButton from "../../Button/SecondaryButton";
import AddressModal from "../../Modal/AddressModal/AddressModal";
import { useLoadScript } from "@react-google-maps/api";
import { Address } from "../../../@types/address";
import addressServiceInstance from "../../../APIs/address";


interface AddressInfoCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  onEdit?: () => void;
  onDelete?: () => void;
}

const AddressInfoCard: React.FC<AddressInfoCardProps> = ({
  title,
  value,
  icon,
  onEdit,
  onDelete,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 flex items-center mb-4 relative">
      <div className="bg-[#75A7AD] bg-opacity-10 p-3 rounded-full mr-4">
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-gray-500 text-sm font-medium">{title}</p>
        <p className="text-gray-800 font-semibold">{value || "Not provided"}</p>
      </div>
      
    </div>
  );
};

const MyAddress: React.FC = () => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyB1rIsa_A5keNvPCSqAzVsY92LgLRmfTmI",
    libraries: ["places"],
  });

  const { notify } = useNotification();
  const { user,  } = useAuth();
  const [longitude, setLongitude] = useState<number>(0);
  const [latitude, setLatitude] = useState<number>(0);
  const [placeId, setPlaceId] = useState<string>("");
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isUpdatingAddress, setIsUpdatingAddress] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [special_instruction, setSpecialInstruction] = useState<string>("");
  const [address_2, setAddress_2] = useState<string>("");
  const [isCreatingNew, setIsCreatingNew] = useState<boolean>(false);
  const [currentAddressIndex, setCurrentAddressIndex] = useState<number>(-1);

  const [
    businessParkOrResidentialComplexName,
    setBusinessParkOrResidentialComplexName,
  ] = useState<string>("");


  const [addresses, setAddresses] = useState<Address[]>([]);
  const [city, setCity] = useState<string>("");
  const [province, setProvince] = useState<string>("");
  const [postalCode, setPostalCode] = useState<string>("");
  const [street, setStreet] = useState<string>("");
  const [suburb, setSuburb] = useState<string>("");
  const [address1, setAddress1] = useState<string>("");
  const [address2, setAddress2] = useState<string>("");
  const [address3, setAddress3] = useState<string>("");
  const [userId, setUserId] = useState<number>(0);
  const [addressId, setAddressId] = useState<number>(0);
  const [unitNumber, setUnitNumber] = useState<string>("");
  const [blockNumber, setBlockNumber] = useState<string>("");
  const [blockName, setBlockName] = useState<string>("");
  const [buildingType, setBuildingType] = useState<string>("");
  const [addressType, setAddressType] = useState<string>("");
  const [building_type_name, setBuilding_type_name] = useState<string>("");
  const [street_number, setStreetNumber] = useState<string>("");

  const [buildingNameOrNumber, setBuildingNameOrNumber] = useState<string>("");
  const [blockNameOrNumber, setBlockNameOrNumber] = useState<string>("");
  const [unitNameOrNumber, setUnitNameOrNumber] = useState<string>("");

  
const getUserAddresses = useCallback(
  async (userId: number) => {
    try {
      const response = await addressServiceInstance.getAddressByUserId(
        userId
      );
      if (response.data) {
        // The data appears to be an array already, so don't wrap it in another array
        setAddresses(Array.isArray(response.data) ? response.data : [response.data]);
      } else {
        console.error(response.error);
        setAddresses([]);
      }
    } catch (err) {
      console.error("Failed to fetch user addresses:", err);
      setAddresses([]);
    }
  },
  [setAddresses]
);

  useEffect(() => {
    if (user?.id) {
      getUserAddresses(user?.id);
    }
  }, [user?.id, getUserAddresses, user]);

  const handleOpenModal = (index: number = -1) => {
    if (index >= 0 && addresses[index]) {
      const address = addresses[index];
      setCity(address.city);
      setProvince(address.province);
      setPostalCode(address.postal_code);
      setUserId(address.user_id || 0);
      setAddressId(address.id);
      setSuburb(address.suburb);
      setBlockNumber(address.block_number);
      setBuildingType(address.building_type);
      setAddressType(address.address_type);
      setUnitNumber(address.unit_name_or_number);
      setBlockName(address.building_name_or_number);
      setStreet(address?.address_1 || "");
      setPlaceId(address?.place_id);
      setLongitude(address?.longitude);
      setLatitude(address?.latitude);
      setSpecialInstruction(address?.special_instruction || "");
      setBuilding_type_name(address?.building_type_name || "");
      setStreetNumber(address?.street_number || "");
      setBuildingNameOrNumber(address?.building_name_or_number || "");
      setBlockNameOrNumber(address?.block_name_or_number || "");
      setUnitNameOrNumber(address?.unit_name_or_number || "");
      setCurrentAddressIndex(index);
      setIsCreatingNew(false);
      setIsUpdatingAddress(true)
    } else {
      // Reset form for new address
      setStreet("");
      setCity("");
      setProvince("");
      setPostalCode("");
      setUserId(user?.id || 0);
      setAddressId(0);
      setSuburb("");
      setSpecialInstruction("");
      setBuildingNameOrNumber("");
      setBlockNameOrNumber("");
      setUnitNameOrNumber("");
      setBuildingNameOrNumber("");
      setCurrentAddressIndex(-1);
      setIsCreatingNew(true);
    }

    setIsModalVisible(true);
    // setIsUpdatingAddress(true)
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setIsUpdatingAddress(false)
    setIsCreatingNew(false);
  };



  const handleUpdateAddress = async () => {
    setIsProcessing(true);
    try {
      const addressData: Address = {
        address_1: street,
        city: city,
        province: province,
        country: "ZA",
        postal_code: postalCode,
        user_id: userId,
        id: addressId,
        address_type: addressType,
        building_type: buildingType,
        unit_number: unitNumber,
        block_number: blockNumber,
        suburb: suburb,
        block_name: blockName,
        special_instruction,
        longitude,
        latitude,
        place_id: placeId,
        building_type_name,
        street_number,
        building_name_or_number: buildingNameOrNumber,
        block_name_or_number: blockNameOrNumber,
        unit_name_or_number: unitNameOrNumber,
        business_park_or_residential_complex_name:
          businessParkOrResidentialComplexName,
        street_name: blockName,
      };

      let response;
      if (isCreatingNew) {
        response = await addressServiceInstance.createAddress(addressData);
      } else {
        response = await addressServiceInstance.editAddress(
          addressData.id,
          addressData
        );
      }

      if (response.data) {
        notify(`Address successfully ${isCreatingNew ? 'created' : 'updated'}`, "success");
        getUserAddresses(user?.id || 0);
        handleCloseModal();
      } else {
        message.error(`Error ${isCreatingNew ? 'creating' : 'updating'} address. No data received from server.`);
        notify(`Error ${isCreatingNew ? 'creating' : 'updating'} address. Please try again.`, "error");
      }
    } catch (error: any) {
      console.error(`Failed to ${isCreatingNew ? 'create' : 'update'} address:`, error);
      message.error(
        `Error ${isCreatingNew ? 'creating' : 'updating'} address: ${
          error?.response?.data?.message || "Unknown error"
        }`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  
const handleDeleteAddress = async (addressId: number) => {
  setIsProcessing(true);
  try {
    const response = await addressServiceInstance.deleteAddress(addressId);
    
    if (response.error === null) {
      // Success case - check for absence of error rather than presence of data
      notify("Address successfully deleted", "success");
      getUserAddresses(user?.id || 0);
    } else {
      // Error case
      message.error(`Error deleting address: ${response.error}`);
      notify("Error deleting address. Please try again.", "error");
    }
  } catch (error: any) {
    console.error("Failed to delete address:", error);
    message.error(
      `Error deleting address: ${
        error?.response?.data?.message || "Unknown error"
      }`
    );
  } finally {
    setIsProcessing(false);
  }
};


  return (
    <div>
      {addresses.length > 0 ? (
        <div className="w-full bg-gray-50 rounded-xl overflow-hidden">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">Delivery Addresses</h3>
              <button 
                onClick={() => handleOpenModal()}
                className="flex items-center text-[#75A7AD] hover:text-[#5a8389]"
              >
                <Edit2 size={16} className="mr-1" />
                <span>Add New Address</span>
              </button>
            </div>
            
            <div className="space-y-6">
              {addresses.map((address, index) => (
                <div key={address.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold text-gray-700">
                      {address.address_type === 'HOME' ? 'Home' : 
                       address.address_type === 'WORK' ? 'Work' : 
                       'Other'} Address
                    </h4>
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleOpenModal(index)}
                        className="text-[#75A7AD] hover:text-[#5a8389]"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        onClick={() => handleDeleteAddress(address.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <AddressInfoCard
                      title="Street Address"
                      value={address.address_1}
                      icon={<MapPin size={20} className="text-[#75A7AD]" />}
                    />
                    <AddressInfoCard
                      title="Building Name"
                      value={address.building_name_or_number}
                      icon={<Building size={20} className="text-[#75A7AD]" />}
                    />
                    <AddressInfoCard
                      title="Suburb"
                      value={address.suburb}
                      icon={<Home size={20} className="text-[#75A7AD]" />}
                    />
                    <AddressInfoCard
                      title="City"
                      value={address.city}
                      icon={<MapPinIcon size={20} className="text-[#75A7AD]" />}
                    />
                    <AddressInfoCard
                      title="Province"
                      value={address.province}
                      icon={<MapPin size={20} className="text-[#75A7AD]" />}
                    />
                    <AddressInfoCard
                      title="Postal Code"
                      value={address.postal_code}
                      icon={<MapPin size={20} className="text-[#75A7AD]" />}
                    />
                  </div>
                 
                </div>
              ))}
            </div>
          </div>

          <AddressModal
            isModalVisible={isModalVisible}
            onClose={handleCloseModal}
            street={street}
            address2={address2}
            address3={address3}
            city={city}
            province={province}
            postalCode={postalCode}
            isProcessing={isProcessing}
            setStreet={setStreet}
            setAddress1={setAddress1}
            setAddress2={setAddress2}
            setAddress3={setAddress3}
            setCity={setCity}
            setPostalCode={setPostalCode}
            setProvince={setProvince}
            handleUpdateAddress={handleUpdateAddress}
            visible={false}
            suburb={suburb}
            setSuburb={setSuburb}
            addressType={addressType}
            setAddressType={setAddressType}
            buildingType={buildingType}
            setBuildingType={setBuildingType}
            unitNumber={unitNumber}
            setUnitNumber={setUnitNumber}
            blockNumber={blockNumber}
            setBlockNumber={setBlockNumber}
            setBlockName={setBlockName}
            blockName={blockName}
            setAddress_2={setAddress_2}
            address_2={address_2}
            place_id={placeId}
            setLatitude={setLatitude}
            setLongitude={setLongitude}
            latitude={latitude}
            longitude={longitude}
            setPlaceId={setPlaceId}
            building_type_name={building_type_name}
            setBuilding_type_name={setBuilding_type_name}
            street_number={street_number}
            setStreetNumber={setStreetNumber}
            businessParkOrResidentialComplexName={businessParkOrResidentialComplexName}
            setBusinessParkOrResidentialComplexName={setBusinessParkOrResidentialComplexName}
            buildingNameOrNumber={buildingNameOrNumber}
            setBuildingNameOrNumber={setBuildingNameOrNumber}
            blockNameOrNumber={blockNameOrNumber}
            setBlockNameOrNumber={setBlockNameOrNumber}
            unitNameOrNumber={unitNameOrNumber}
            setUnitNameOrNumber={setUnitNameOrNumber} 
            isCreatingAddress={isCreatingNew}
            handleCreateAddress={handleUpdateAddress}  
            isUpdatingAddress={isUpdatingAddress}        
          />
        </div>
      ) : (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className="bg-[#75A7AD] bg-opacity-10 p-6 rounded-full mx-auto mb-4 w-20 h-20 flex items-center justify-center">
            <MapPin size={32} className="text-[#75A7AD]" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No Addresses Found</h3>
          <p className="text-gray-500 mb-6">You haven't added any delivery addresses yet.</p>
          <SecondaryButton onClick={() => handleOpenModal()} label="Add New Address" />
        </div>
      )}
    </div>
  );
};

export default MyAddress;